import { Target, TwoFactorTargetEnv } from '../types';

/* *** v1 legacy types. Migrate to v2 below. *** */
/* *** ------------------------------------- *** */

/** @deprecated Use TargetConfig instead */
export type TwoFactorTargetConfig = {
  retailHost: string;
  lccHost: string;
  vccHost: string;
  accHost: string;
  helpCenterHost: string;
};

/** @deprecated Use TargetConfigMapping instead */
export const TwoFactorTargetConfigMapping: Readonly<
  Record<TwoFactorTargetEnv, TwoFactorTargetConfig>
> = {
  [TwoFactorTargetEnv.Production]: {
    retailHost: 'https://www.coinbase.com',
    lccHost: 'https://login.coinbase.com',
    vccHost: 'https://verify.coinbase.com',
    accHost: 'https://accounts.coinbase.com',
    helpCenterHost: 'https://help.coinbase.com',
  },
  [TwoFactorTargetEnv.Staging]: {
    retailHost: 'https://coinbase-staging.cbhq.net',
    lccHost: 'https://login-staging.cbhq.net',
    vccHost: 'https://verify-staging.cbhq.net',
    accHost: 'https://accounts-staging.cbhq.net',
    helpCenterHost: 'https://helpcenter-staging.cbhq.net',
  },
  [TwoFactorTargetEnv.Development]: {
    retailHost: 'https://coinbase-dev.cbhq.net',
    lccHost: 'https://login-dev.cbhq.net',
    vccHost: 'https://verify-dev.cbhq.net',
    accHost: 'https://accounts-dev.cbhq.net',
    helpCenterHost: 'https://helpcenter-dev.cbhq.net',
  },
  [TwoFactorTargetEnv.Local]: {
    /**
     * API host and FE host need to be the same since we're using
     * http-proxy-middleware to proxy BE requests locally.
     * The proxy also takes place in coinbase-www.
     */
    retailHost: 'http://localhost:3000',
    lccHost: 'http://localhost:3000',
    vccHost: 'http://localhost:3000',
    accHost: 'https://accounts-dev.cbhq.net',
    helpCenterHost: 'https://helpcenter-dev.cbhq.net',
  },
  [TwoFactorTargetEnv.Mock]: {
    retailHost: 'https://coinbase-dev.cbhq.net',
    lccHost: 'http://localhost:3000',
    vccHost: 'http://localhost:3000',
    accHost: 'https://accounts-dev.cbhq.net',
    helpCenterHost: 'https://helpcenter-dev.cbhq.net',
  },
};

/* *** v2 refactor of the types above with more general naming *** */
/* *** ------------------------------------------------------- *** */
export type TargetConfig = {
  retailHost: string;
  lccHost: string;
  lccAsiaHost: string;
  vccHost: string;
  accHost: string;
  helpCenterHost: string;
};

export const TargetConfigMapping: Readonly<Record<Target, TargetConfig>> = {
  production: {
    retailHost: 'https://www.coinbase.com',
    lccHost: 'https://login.coinbase.com',
    lccAsiaHost: 'https://login-asia.coinbase.com',
    vccHost: 'https://verify.coinbase.com',
    accHost: 'https://accounts.coinbase.com',
    helpCenterHost: 'https://help.coinbase.com',
  },
  staging: {
    retailHost: 'https://coinbase-staging.cbhq.net',
    lccHost: 'https://login-staging.cbhq.net',
    lccAsiaHost: 'https://login-asia-staging.cbhq.net',
    vccHost: 'https://verify-staging.cbhq.net',
    accHost: 'https://accounts-staging.cbhq.net',
    helpCenterHost: 'https://helpcenter-staging.cbhq.net',
  },
  development: {
    retailHost: 'https://coinbase-dev.cbhq.net',
    lccHost: 'https://login-dev.cbhq.net',
    lccAsiaHost: 'https://login-asia-dev.cbhq.net',
    vccHost: 'https://verify-dev.cbhq.net',
    accHost: 'https://accounts-dev.cbhq.net',
    helpCenterHost: 'https://helpcenter-dev.cbhq.net',
  },
  local: {
    /**
     * API host and FE host need to be the same since we're using
     * http-proxy-middleware to proxy BE requests locally.
     * The proxy also takes place in coinbase-www.
     */
    retailHost: 'http://localhost:3000',
    lccHost: 'http://localhost:3000',
    lccAsiaHost: 'http://localhost:3000',
    vccHost: 'http://localhost:3000',
    accHost: 'https://accounts-dev.cbhq.net',
    helpCenterHost: 'https://helpcenter-dev.cbhq.net',
  },
  mock: {
    retailHost: 'https://coinbase-dev.cbhq.net',
    lccHost: 'http://localhost:3000',
    lccAsiaHost: 'http://localhost:3000',
    vccHost: 'http://localhost:3000',
    accHost: 'https://accounts-dev.cbhq.net',
    helpCenterHost: 'https://helpcenter-dev.cbhq.net',
  },
};

/**
 * Returns the appropriate base URL based on user's timezone.
 * Routes Asia timezone users to the Asia-specific host unless the kill switch is active.
 *
 * @param defaultUrl - The default URL to use for non-Asia timezones or when kill switch is active
 * @param asiaUrl - The Asia-specific URL to use for Asia timezone users
 * @param killMultiRegion - Kill switch to disable multi-region routing (defaults to false)
 * @returns The appropriate base URL based on timezone and kill switch state
 */
export function getBaseUrlForTimezone(
  defaultUrl: string,
  asiaUrl: string,
  killMultiRegion?: boolean,
): string {
  if (killMultiRegion) {
    return defaultUrl;
  }

  const tz = Intl?.DateTimeFormat()?.resolvedOptions().timeZone;
  return tz?.includes('Asia') ? asiaUrl : defaultUrl;
}
